import { minBy, omit } from 'es-toolkit';

import { assertUnlessInProduction } from '@paragon/client-tools';
import { BevelCutTypePreference, TrussEnvelope } from '@paragon/contract-library';
import {
  Direction3D,
  distanceEquals,
  intersectSpanSets,
  Line2D,
  LineSegment2D,
  Polygon2D,
  Polygon3D,
  reduceAndSortSpans,
  Span,
} from '@paragon/geometry-library';
import { subtractSpanSets } from '@paragon/geometry-library/source/scripts/Span';
import {
  calculateBoundedCeilingPlane,
  calculateRoofContainerSolid,
  createDisjointRoofBasePolygonFromEdges,
  createRoofBasePolygonsFromPolygons,
  getOffsetVectorForEnvelopeSegment,
  getRoofPlaneGeometriesWithCache,
  TrussEnvelopeCuts,
} from '@paragon/layout';
import { groupTransitively, notNullish, pickBy, ReadonlyRecord } from '@paragon/utilities';

import { InsertingTrussEnvelopeData } from '../../../../store/slices/applicationSlice';
import { GeometricEcosystem } from '../../ecosystem/GeometricEcosystem';
import { RoofContainerBasedGeometricEcosystem } from '../../ecosystem/RoofContainerBasedGeometricEcosystem';
import { RoofPlaneBasedGeometricEcosystem } from '../../ecosystem/RoofPlaneBasedGeometricEcosystem';
import { TrussEnvelopeData } from '../../ecosystem/TrussEnvelopeData';
import { EnvelopeSpan } from './EnvelopeSpan';
import { StageOneTrussEnvelope } from './stage1';
import { StageThreeEnvelopeBevelCuts, StageThreeTrussEnvelope } from './stage3';

/**
 * During the second stage of truss calculation, the envelope's defining line is turned into a line segment based on the
 *   earlier chosen defining point and truss thickness.
 */
export class StageTwoTrussEnvelope {
  source: StageOneTrussEnvelope;

  envelopeData: InsertingTrussEnvelopeData;

  /**
   * The segment that is being proposed as the "correct" truss envelope segment in the previously calculated `line`,
   *   based on the user's mouse position and the other objects in the layout.
   */
  segment: LineSegment2D;

  /** Unless overridden by cap levels later, the elevation that will be assigned to the resulting truss envelope. */
  elevation: number;

  leftOverhang: number;
  rightOverhang: number;

  boundingTrussEnvelopes: ReadonlyRecord<string, TrussEnvelopeData>;

  private constructor(
    source: StageOneTrussEnvelope,
    envelopeData: InsertingTrussEnvelopeData,
    segment: LineSegment2D,
    elevation: number,
    leftOverhang: number,
    rightOverhang: number,
    boundingTrussEnvelopes: ReadonlyRecord<string, TrussEnvelopeData>,
  ) {
    this.source = source;
    this.envelopeData = envelopeData;
    this.segment = segment;
    this.elevation = elevation;
    this.leftOverhang = leftOverhang;
    this.rightOverhang = rightOverhang;
    this.boundingTrussEnvelopes = boundingTrussEnvelopes;
  }

  /**
   * If the current envelope has top or bottom cuts, or is constrained by cap levels, we attempt to narrow the roof area
   * and bounding truss envelopes to only include the roof areas and truss envelope on the side of the cuts where this
   * truss envelope exists. This behavior is useful for the user when inserting a valley or cap set so that this truss
   * envelope doesn't get bound by truss envelopes it might be sitting on top of and so that the left and right points
   * of the truss envelope get trimmed to match the resulting 3D solid after cuts have been applied.
   */
  private static narrowEcosystemForTopBottomCutsAndCapPlanes(
    ecosystem: GeometricEcosystem,
    envelopeData: InsertingTrussEnvelopeData,
  ) {
    const defaultEcosystem = {
      boundingTrussEnvelopes: ecosystem.getTrussEnvelopeDictionary(),
      roofAreas: ecosystem.getRoofAreas(),
    };

    if (!envelopeData.topCuts.length && !envelopeData.bottomCuts.length && envelopeData.containerLevel === 'NotCapped')
      return defaultEcosystem;

    const areasAndCuts =
      ecosystem instanceof RoofContainerBasedGeometricEcosystem
        ? StageTwoTrussEnvelope.narrowRoofContainerRoofAreas(ecosystem, envelopeData)
        : ecosystem instanceof RoofPlaneBasedGeometricEcosystem
          ? StageTwoTrussEnvelope.narrowRoofPlaneRoofAreas(ecosystem, envelopeData)
          : assertUnlessInProduction('Unknown ecosystem type');

    if (!areasAndCuts) return defaultEcosystem;
    const { roofAreas, allCuts } = areasAndCuts;

    const boundingTrussEnvelopes = pickBy(
      ecosystem.getTrussEnvelopeDictionary(),
      ({ solid }) => !solid || solid.vertices.some((v) => allCuts.every((cut) => cut.pointIsOnNormalSide(v))),
    );

    return { boundingTrussEnvelopes, roofAreas };
  }

  public static narrowRoofAreasForTopBottomCutsAndCapPlanes(ecosystem: GeometricEcosystem, envelope: TrussEnvelope) {
    return this.narrowEcosystemForTopBottomCutsAndCapPlanes(ecosystem, envelope).roofAreas;
  }

  private static narrowRoofContainerRoofAreas(
    ecosystem: RoofContainerBasedGeometricEcosystem,
    envelopeData: InsertingTrussEnvelopeData,
  ) {
    const activeRoofContainer = ecosystem.getActiveRoofContainer();

    if (activeRoofContainer.solidData === null) return null;

    const trussEnvelopeCuts = TrussEnvelopeCuts.fromTrussEnvelope(
      envelopeData,
      () => {
        throw new Error('Cannot use roof plane cuts in a roof container layout.');
      },
      () => {
        throw new Error('Cannot use ceiling plane cuts in a roof container layout.');
      },
    );
    if (!trussEnvelopeCuts) return null;

    const roofPolygons = calculateRoofContainerSolid(activeRoofContainer)!.trimByMany(trussEnvelopeCuts.planeCuts);

    return {
      roofAreas: roofPolygons.flatMap(createRoofBasePolygonsFromPolygons),
      allCuts: trussEnvelopeCuts.planeCuts,
    };
  }

  private static narrowRoofPlaneRoofAreas(
    ecosystem: RoofPlaneBasedGeometricEcosystem,
    envelopeData: InsertingTrussEnvelopeData,
  ) {
    const { roofPlanes, ceilingPlanes, ceilingTrays, bearingEnvelopes } = ecosystem.source;
    const { roofFaces, ceilingFaces, inferredCeilingFaces, getTrussEnvelopeCuts } = getRoofPlaneGeometriesWithCache(
      roofPlanes,
      ceilingPlanes,
      ceilingTrays,
      bearingEnvelopes,
      envelopeData.levelGuid,
    )!;

    const trussEnvelopeCuts = getTrussEnvelopeCuts(envelopeData);
    if (!trussEnvelopeCuts) return null;

    const getGroupedPlaneFootprints = (planes: readonly Polygon3D[]) =>
      groupTransitively(
        planes.flatMap((shape) => shape.sliceOrientedByMany(trussEnvelopeCuts.planeCuts)),
        (p1, p2) => p1.edges.some((e1) => p2.edges.some((e2) => e1.overlaps(e2))),
      ).flatMap((group) =>
        createDisjointRoofBasePolygonFromEdges(
          group
            .map((polygon) => polygon.projectTo2D())
            .filter(notNullish)
            .flatMap(({ edges }) => edges),
        ),
      );

    // bottom cuts narrow the roof planes, top cuts narrow the ceiling planes
    const narrowedRoofPlaneAreas = getGroupedPlaneFootprints(roofFaces);
    const narrowedCeilingPlaneAreas = getGroupedPlaneFootprints([...ceilingFaces, ...inferredCeilingFaces]);

    const anyTopCuts = trussEnvelopeCuts.planeCuts.some((cut) =>
      cut.halfSpace.direction.isOrientedWith(Direction3D.negativeZ),
    );
    const anyBottomCuts = trussEnvelopeCuts.planeCuts.some((cut) =>
      cut.halfSpace.direction.isOrientedWith(Direction3D.positiveZ),
    );

    const roofAreas = anyBottomCuts
      ? anyTopCuts
        ? // If there are both top and bottom cuts, the roof area is no longer determined by the presence of roof
          // geometries, and in fact may be a non-closed shape. What we currently return is a heuristic.
          narrowedRoofPlaneAreas
        : narrowedRoofPlaneAreas
      : anyTopCuts
        ? narrowedCeilingPlaneAreas
        : narrowedRoofPlaneAreas
            .flatMap((roofArea) =>
              narrowedCeilingPlaneAreas.map((ceilingArea) =>
                // FIXME polygonal intersection is desired here, but implementing that on the client is currently
                // prohibitive. On the server we use Clipper to perform polygonal intersection. This heuristic is designed
                // specifically with roof areas in mind and is definitely not a good general-purpose heuristic.
                roofArea.touchesOrOverlaps(ceilingArea) ? minBy([roofArea, ceilingArea], ({ area }) => area) : null,
              ),
            )
            .filter(notNullish);

    return { roofAreas, allCuts: trussEnvelopeCuts.planeCuts };
  }

  public static calculateElevation(
    segment: LineSegment2D,
    leftOverhang: number,
    rightOverhang: number,
    ecosystem: GeometricEcosystem,
    envelopeData: InsertingTrussEnvelopeData,
  ) {
    return (
      calculateBoundedCeilingPlane(
        { ...envelopeData, leftPoint: segment.basePoint, rightPoint: segment.endPoint, leftOverhang, rightOverhang },
        // FIXME: Project hierarchy not respected
        Object.values(ecosystem.getBearingEnvelopes()),
      )?.centroid.z ?? 0
    );
  }

  /**
   * Creates a stage two envelope, which involves calculating the envelope's defining segment and performing the
   *   relevant adjustments.
   */
  public static fromStageOneEnvelopeAndThickness(
    source: StageOneTrussEnvelope,
    envelopeData: InsertingTrussEnvelopeData,
  ) {
    const { boundingTrussEnvelopes, roofAreas } = this.narrowEcosystemForTopBottomCutsAndCapPlanes(
      source.ecosystem,
      envelopeData,
    );

    const boundingTrussEnvelopesOtherThanSelf = omit(boundingTrussEnvelopes, [envelopeData.guid]);

    const intersections = (['Back', 'Center', 'Front'] as const)
      .map((justification) =>
        findSpansAlongDefinitionLine(
          source,
          envelopeData,
          source.line.translate(getOffsetVectorForEnvelopeSegment(source.line.direction, envelopeData, justification)),
          boundingTrussEnvelopesOtherThanSelf,
          roofAreas,
        ),
      )
      .reduce(intersectSpanSets)
      .map((intersectionAlongLine) => ({
        segment: new LineSegment2D(
          source.line.getPointAlongLine(intersectionAlongLine.start),
          source.line.getPointAlongLine(intersectionAlongLine.end),
        ),
        leftOverhang: intersectionAlongLine.leftOverhang,
        rightOverhang: intersectionAlongLine.rightOverhang,
      }));

    const segmentAndOverhangs = minBy(intersections, ({ segment }) =>
      source.definingPoint.distanceToLineSegment(segment),
    );
    if (segmentAndOverhangs == null) return null;
    const { segment, leftOverhang, rightOverhang } = segmentAndOverhangs;

    const elevation = StageTwoTrussEnvelope.calculateElevation(
      segment,
      leftOverhang,
      rightOverhang,
      source.ecosystem,
      envelopeData,
    );

    return new StageTwoTrussEnvelope(
      source,
      envelopeData,
      segment,
      elevation,
      leftOverhang,
      rightOverhang,
      boundingTrussEnvelopesOtherThanSelf,
    );
  }

  /**
   * Progresses to the next stage of envelope calculation, where bevel cuts are calculated and the length will be
   *   further adjusted based on the calculated bevel cuts.
   */
  adjustToAccountForBevels(bevelCutTypePreference: BevelCutTypePreference, bevelCuts?: StageThreeEnvelopeBevelCuts) {
    return StageThreeTrussEnvelope.fromStageTwoEnvelope(this, bevelCuts, bevelCutTypePreference);
  }
}

interface Splittable<T> {
  splitAt(distance: number): T[];
}

/**
 * Takes a truss envelope line and finds the spans where a valid truss envelope could be placed.
 * @param envelopeDefinitionLine The envelope line that we're finding spans for.
 * @param envelopeLines The "back", "center", and "front" lines for this truss envelope. Needed for bounding against
 * bounding segments.
 * @returns The spans defined by distances along the line.
 */
function findSpansAlongDefinitionLine(
  source: StageOneTrussEnvelope,
  envelopeData: InsertingTrussEnvelopeData,
  envelopeDefinitionLine: Line2D,
  boundingTrussEnvelopes: ReadonlyRecord<string, TrussEnvelopeData>,
  roofAreas: readonly Polygon2D[],
) {
  let validEnvelopeSpans = reduceAndSortSpans(
    roofAreas.flatMap((roofArea) => Span.fromIntersectionsOfLineAndPolygon(envelopeDefinitionLine, roofArea)),
  );

  const boundingGeometries = Object.values(boundingTrussEnvelopes).flatMap((data) => [
    data.backSegmentWithBevelsConsidered,
    data.frontSegmentWithBevelsConsidered,
  ]);

  if (envelopeData.definitionType === 'FeaturePattern' && envelopeData.featurePatternContainerGuid) {
    const containerPolygon =
      source.ecosystem.getFeaturePatternContainerDictionaryIn2D()[envelopeData.featurePatternContainerGuid];
    if (containerPolygon) boundingGeometries.push(...containerPolygon.edges);

    boundingGeometries.push(...Object.values(source.ecosystem.getBearingAreas()).flatMap((bearing) => bearing.edges));
  }

  const boundSpansByGeometries = <T extends Splittable<T>>(
    spans: readonly T[],
    boundingGeometries: readonly (Line2D | LineSegment2D)[],
  ) =>
    boundingGeometries.reduce((boundedSpans, boundingGeometry) => {
      const boundingLine = boundingGeometry instanceof Line2D ? boundingGeometry : boundingGeometry.toLine();
      const intersection = boundingLine.intersectWithLine(envelopeDefinitionLine);

      if (intersection == null) return boundedSpans;
      if (boundingGeometry instanceof LineSegment2D && !boundingGeometry.containsOnInside(intersection))
        return boundedSpans;

      const distanceAlong = intersection.projectedSignedDistanceAlong(source.line);
      return boundedSpans.flatMap((span) => span.splitAt(distanceAlong));
    }, spans.slice());

  validEnvelopeSpans = boundSpansByGeometries(validEnvelopeSpans, boundingGeometries);

  if (envelopeData.definitionType === 'FeaturePattern') {
    const bearingSpans = reduceAndSortSpans(
      Object.values(source.ecosystem.getBearingAreas()).flatMap((bearingArea) =>
        Span.fromIntersectionsOfLineAndPolygon(envelopeDefinitionLine, bearingArea),
      ),
    );

    validEnvelopeSpans = validEnvelopeSpans.map((span) =>
      span.growIntoAdjacentSpans({
        onLeft: bearingSpans.find((bearingSpan) => distanceEquals(bearingSpan.end, span.start)),
        onRight: bearingSpans.find((bearingSpan) => distanceEquals(span.end, bearingSpan.start)),
      }),
    );
  }

  let validEnvelopeSpans2 = validEnvelopeSpans.map(EnvelopeSpan.fromSpan);

  if (envelopeData.componentType === 'RoofTruss') {
    const overhangSpans = boundSpansByGeometries(
      subtractSpanSets(
        reduceAndSortSpans(
          source.ecosystem
            .getOverhangAreas()
            .flatMap((overhangArea) => Span.fromIntersectionsOfLineAndPolygon(envelopeDefinitionLine, overhangArea)),
        ),
        validEnvelopeSpans,
      ),
      boundingGeometries,
    );

    validEnvelopeSpans2 = validEnvelopeSpans2.map((span) =>
      span.growIntoAdjacentOverhangSpans({
        onLeft: overhangSpans.find((overhangSpan) => distanceEquals(overhangSpan.end, span.span.start)),
        onRight: overhangSpans.find((overhangSpan) => distanceEquals(overhangSpan.start, span.span.end)),
      }),
    );
  }

  return boundSpansByGeometries(validEnvelopeSpans2, source.ecosystem.getBoundingWorkLines(envelopeData.levelGuid));
}
